import React from 'react';
import {
  Box, Typography, Grid, Card, CardActionArea, CardContent, useTheme, alpha, Chip,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HubIcon from '@mui/icons-material/Hub';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { SUPPORTED_FRAMEWORKS } from './utils/frameworkSchemas';

/**
 * FrameworkPicker
 *
 * Step 1 of the wizard. Visual cards for each supported framework.
 * Uses ONLY theme tokens (palette.primary, palette.divider,
 * palette.text, etc.) so it adapts to light + dark themes
 * automatically and stays brand-consistent. No hardcoded colors.
 */
const FrameworkPicker = ({ value, onChange }) => {
  const theme = useTheme();

  const frameworks = [
    {
      name: 'TensorFlow',
      Icon: HubIcon,
      tagline: 'Production deep learning',
      description: 'Mature Keras pipeline. The recommended default for forecasting models.',
      bestFor: ['Forecasting', 'Sequences', 'Production'],
    },
    {
      name: 'PyTorch',
      Icon: LocalFireDepartmentIcon,
      tagline: 'Flexible neural networks',
      description: 'PyTorch backend with the same layer schema as TensorFlow. Native TorchScript export.',
      bestFor: ['Transformers', 'Research', 'Custom layers'],
    },
    {
      name: 'Scikit-learn',
      Icon: ScatterPlotIcon,
      tagline: 'Tabular ML, fast baselines',
      description: 'Pick an estimator and tune hyperparameters. No layer editor needed.',
      bestFor: ['Tabular', 'Baselines', 'Interpretable'],
    },
    {
      name: 'XGBoost',
      Icon: AccountTreeIcon,
      tagline: 'Gradient-boosted trees',
      description: 'High-performance boosting. The FIL backend serves single-output models extremely fast.',
      bestFor: ['Tabular', 'High accuracy', 'Trees'],
    },
  ];

  const visibleFrameworks = frameworks.filter((f) => SUPPORTED_FRAMEWORKS.includes(f.name));

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Choose a framework
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
          The framework determines how your model is built, trained, and served. You can change it later by editing the model.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {visibleFrameworks.map((fw) => {
          const isSelected = value === fw.name;
          const Icon = fw.Icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={fw.name}>
              <Card
                variant="outlined"
                sx={{
                  borderWidth: isSelected ? 2 : 1,
                  borderColor: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.04)
                    : theme.palette.background.paper,
                  height: '100%',
                  position: 'relative',
                  transition: theme.transitions.create(
                    ['border-color', 'background-color', 'box-shadow', 'transform'],
                    { duration: theme.transitions.duration.shortest }
                  ),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.lightBoxShadow,
                  },
                }}
              >
                {isSelected && (
                  <CheckCircleRoundedIcon
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      color: theme.palette.primary.main,
                      fontSize: 22,
                    }}
                  />
                )}
                <CardActionArea
                  onClick={() => onChange(fw.name)}
                  sx={{ height: '100%', alignItems: 'stretch' }}
                >
                  <CardContent sx={{ p: 3, height: '100%' }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        bgcolor: isSelected
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.08),
                        color: isSelected
                          ? theme.palette.primary.contrastText
                          : theme.palette.primary.main,
                        transition: theme.transitions.create(['background-color', 'color']),
                      }}
                    >
                      <Icon sx={{ fontSize: 28 }} />
                    </Box>

                    <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, fontSize: '18px' }}>
                      {fw.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        mb: 1.25,
                      }}
                    >
                      {fw.tagline}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                      {fw.description}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {fw.bestFor.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontSize: '11px',
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.secondary,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default FrameworkPicker;
